import { inject, provide, ref, watch, type Ref } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

export interface FeedbackContext {
  /** Which page the feedback is about. App.vue derives it from the route. */
  type?: string
  uuid?: string
  title?: string
  subject?: string
}

const FeedbackContextKey = Symbol('FeedbackContext')

export function provideFeedbackContext(initialContext: FeedbackContext = {}) {
  const context: Ref<FeedbackContext> = ref(initialContext)
  
  const updateContext = (updates: Partial<FeedbackContext>) => {
    context.value = { ...context.value, ...updates }
  }
  
  provide(FeedbackContextKey, {
    context,
    updateContext
  })
  
  return { context, updateContext }
}

export function useUserFeedbackContext() {
  const injected = inject<{
    context: Ref<FeedbackContext>
    updateContext: (updates: Partial<FeedbackContext>) => void
  }>(FeedbackContextKey, {
    context: ref({}),
    updateContext: () => {}
  })
  
  return injected
}

/**
 * Provides the context and keeps it in step with the route: each navigation
 * labels the feedback with the page and clears the previous page's per-item
 * fields. Without that clearing the context outlives the page that set it, and
 * one page's uuid/title/subject rides along on feedback submitted from every
 * page visited afterwards.
 *
 * Watches the path rather than the full path, so a page that keeps its state in
 * the query string does not lose its context on every filter change.
 */
export function provideRouteFeedbackContext(route: RouteLocationNormalizedLoaded) {
  const api = provideFeedbackContext()

  watch(
    () => route.path,
    () => {
      api.updateContext({
        type: route.meta.feedbackType || route.meta.title,
        uuid: undefined,
        title: undefined,
        subject: undefined
      })
    },
    { immediate: true }
  )

  return api
}
